/* מנוע התזרים: מהיתרה של היום, יום אחרי יום, עם כל מה שידוע מראש:
   הכנסות וחיובים קבועים, חיוב כרטיס האשראי, תשלומים ידועים, ותנועות עו"ש שכבר נרשמו לתאריך עתידי.
   הוצאות שוטפות שעוד לא קרו (סופר, דלק) לא בתחזית של התקופה הנוכחית. בשבילן יש "אפשר להוציא".
   בתקופות הבאות: באשראי הן בתוך הערכת חיוב הכרטיס. בעו"ש (ביט, מזומן) רק במצב מחמיר, כהערכה שבועית (cashVarTypical). */
import { S, memo } from './state.js';
import { fISO, fDim, todayISO, iso10, dDays, absAmt, wD, median } from '../lib/format.js';
import { getTxSrc, isExcluded, isCardChargeRow } from './tx.js';
import { curKey, keyAdd, keyOfISO, periodStart, periodEnd, periodLength } from './periods.js';
import { balToday, balEst, lateItems, hasBalance } from './balance.js';
import { cardChargeEvents, cardChargeAmount, cardFutureEstimate, cardTemplate } from './card.js';
import { recOcc, recEst, recFires, recEvery, recFreqLabel, recMatchAll, fixedMatcher } from './recurring.js';
import { isSavingRec } from './savings.js';

export const STRESS = 1.15;

/** מצב מחמיר: גם הוצאות שוטפות מהעו"ש בתחזית לתקופות הבאות, בעודף החודשי ובכרית. מצב רגיל: רק מה שמוגדר וחיוב הכרטיס */
export function strictMode() {
  return S.set.fcMode === 'strict';
}

/**
 * @param {number} days ימים קדימה מהיום
 * @param {{stress?:boolean, sim?:Object<string,number>, extra?:Array<{iso:string,amount:number,name:string,type?:string}>}} opts
 *   stress: כל ההוצאות ×1.15. sim: מזהה חיוב קבוע ← יום חדש בחודש (סימולציה, לא נשמר)
 *   extra: תנועות היפותטיות ("אפשר לקנות את זה?"). תאריך שעבר נכנס מחר
 * @returns {{date:Date,key:string,balance:number,events:Array}[]}
 */
export function project(days, opts = {}) {
  const stress = !!opts.stress, sim = opts.sim || {}, extra = opts.extra || [];
  return memo('project|' + days + '|' + stress + '|' + JSON.stringify(sim) + '|' + JSON.stringify(extra) + '|' + (strictMode() ? 's' : ''), () => projectRaw(days, stress, sim, extra));
}

function projectRaw(days, stress, sim, extra) {
  const startStr = todayISO(), start = new Date(startStr + 'T12:00:00');
  let bal = balToday().amount;

  // תנועות עו"ש שכבר נרשמו לתאריך עתידי
  const byDate = {};
  S.tx.forEach((t) => {
    if (!t.date || getTxSrc(t) === 'credit') return;
    const ds = iso10(t.date);
    if (ds <= startStr) return;
    (byDate[ds] = byDate[ds] || []).push({ name: t.description || 'ללא תיאור', amount: absAmt(t), type: t.type, real: true });
  });

  const cardEv = cardChargeEvents(start, days);
  const cardTpl = cardTemplate();
  /* כשיש חיוב כרטיס בתחזית (בפועל או הערכה), חיובים קבועים בכרטיס, תשלומים ותבנית "ישראכרט"
     כבר בתוכו ולא נספרים שוב */
  const cardHas = !!cardChargeAmount() || cardFutureEstimate().amount > 0;
  const cv = strictMode() ? cashVarTypical() : { amount: 0 }, curEnd = fISO(periodEnd(curKey()));
  let vAcc = 0, vN = 0;
  const out = [];
  for (let i = 1; i <= days; i++) {
    const d = new Date(start.getTime());
    d.setDate(d.getDate() + i);
    const key = fISO(d);
    const evs = (byDate[key] || []).slice();
    if (cardEv[key]) evs.push(cardEv[key]);
    extra.forEach((x) => {
      if (x.iso === key || (i === 1 && x.iso < key)) evs.push({ name: x.name, amount: x.amount, type: x.type || 'expense', extra: true, estimated: !!x.est });
    });
    if (!cardHas) {
      S.commits.forEach((c) => {
        if (c.due_date === key) evs.push({ name: c.description, amount: absAmt(c), type: 'expense', real: true, commit: true });
      });
    }
    S.rec.forEach((r) => {
      const dim = fDim(d.getFullYear(), d.getMonth());
      const day = Math.min(Number(sim[r.id] != null ? sim[r.id] : r.day_of_month) || 1, dim);
      if (d.getDate() !== day || !recFires(r, d)) return;
      // המועד האמיתי (בלי סימולציה): אם כבר נמצאה עסקה שלו, או שסומן שלא ירד, הוא לא נספר שוב
      const natD = new Date(d.getFullYear(), d.getMonth(), Math.min(Number(r.day_of_month) || 1, dim), 12);
      const oc = recOcc(r, fISO(natD));
      if (oc && (oc.st === 'paid' || oc.st === 'skip')) return;
      if (cardHas && (r.source || 'cash') === 'credit') return;
      // שתי תבניות זהות נספרות שתיהן (למשל שני ילדים באותו גן). כפילות אמיתית מוצגת כהתראה (findDupRec)
      if (cardHas && cardTpl && r === cardTpl) return;
      evs.push({
        name: r.description + (recEvery(r) > 1 ? ' · ' + recFreqLabel(r) : ''), amount: oc ? oc.amount : recEst(r),
        type: r.type, real: false, recId: r.id, moved: sim[r.id] != null,
      });
    });
    // מועדים שעברו ועוד לא נקלטו בסריקה: לא בתוך היתרה, ולכן נכנסים מחר
    if (i === 1) {
      lateItems().forEach((x) => {
        if (x.card) evs.push({ name: 'חיוב כרטיס אשראי (טרם נקלט בעו"ש, צפוי היה ' + wD(x.iso) + ')', amount: -x.amount, type: 'expense', card: true, late: true, lateCard: true, cardSrc: 'late', iso: x.iso });
        else evs.push({ name: x.name + ' (' + (x.st === 'missing' ? 'לא נמצא בסריקה' : 'טרם נקלט') + ', צפוי היה ' + wD(x.iso) + ')', amount: Math.abs(x.amount), type: x.amount > 0 ? 'income' : 'expense', recId: x.recId, late: true });
      });
    }
    // הוצאות שוטפות מהעו"ש בתקופות הבאות: מפוזרות לאורך התקופה, שורה אחת לשבוע
    if (cv.amount > 0 && key > curEnd) {
      const k = keyOfISO(key), next = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 12);
      vAcc += cv.amount / periodLength(k);
      vN++;
      if (vN === 7 || i === days || keyOfISO(fISO(next)) !== k) {
        evs.push({ name: 'הוצאות שוטפות מהעו"ש (הערכה ל-' + vN + ' ימים)', amount: Math.round(vAcc), type: 'expense', estimated: true, variable: true });
        vAcc = 0;
        vN = 0;
      }
    }
    evs.forEach((e) => {
      let amt = e.amount;
      if (stress && e.type !== 'income') amt *= STRESS;
      bal += (e.type === 'income' ? 1 : -1) * amt;
    });
    out.push({ date: d, key, balance: bal, events: evs });
  }
  return out;
}

/** ימים מהיום ועד סוף התקופה. ביום האחרון של התקופה: 0 */
export function daysToEnd() {
  return Math.max(0, dDays(periodEnd(curKey()), todayISO()));
}
/** התחזית מהיום עד סוף התקופה הנוכחית, בלי תרחישים */
export function projToEnd() {
  const d = daysToEnd();
  return d > 0 ? project(d) : [];
}
/** הנקודה הנמוכה עד סוף התקופה: אותו מספר כמו בתוכנית ובתזרים ("התקופה הנוכחית").
    אם בתקופה הבאה היא עמוקה יותר, גם אותה, כדי לא להסתיר שפל קרוב. בבית ובעדכון הבוקר בטלגרם */
export function nextLow() {
  const p = projToEnd(), t0 = new Date(todayISO() + 'T12:00:00');
  let low = { balance: balEst().amount, date: t0, idx: -1 };
  p.forEach((x, i) => { if (x.balance < low.balance) low = { ...x, idx: i }; });
  // "לפני המשכורת": הכנסה נכנסת עד 5 ימים אחרי הנקודה הנמוכה
  const beforeIncome = low.idx >= 0 && p.slice(low.idx + 1, low.idx + 6).some((x) => x.events.some((e) => e.type === 'income'));
  const d2 = rangeDays(2), p2 = project(d2), cut = fISO(periodEnd(curKey()));
  let next = null;
  p2.forEach((x) => { if (x.key > cut && (!next || x.balance < next.balance)) next = x; });
  if (next && next.balance >= low.balance - 100) next = null;
  return { ...low, beforeIncome, end: periodEnd(curKey()), next, nextDays: d2 };
}

export function lowPoint(p, fallback) {
  let low = p[0] || fallback;
  p.forEach((x) => { if (x.balance < low.balance) low = x; });
  return low;
}

/** תחזית לתקופה עתידית. null כשהתקופה רחוקה מדי */
export function futurePeriodForecast(K) {
  const s = periodStart(K), e = periodEnd(K);
  const days = dDays(e, todayISO()) + 1;
  if (days < 1 || days > 400) return null;
  const pr = project(days);
  const sISO = fISO(s), eISO = fISO(e);
  let open = balToday().amount, close = null, low = null, lowDate = null, inc = 0, exp = 0, card = 0, nInc = 0, nExp = 0;
  const items = [];
  pr.forEach((x) => {
    if (x.key < sISO) { open = x.balance; return; }
    if (x.key > eISO) return;
    close = x.balance;
    if (low === null || x.balance < low) { low = x.balance; lowDate = x.date; }
    x.events.forEach((v) => {
      // מועד שכבר עבר שייך לתקופה הקודמת, ולכן חלק מהפתיחה
      if (v.late) { open += (v.type === 'income' ? 1 : -1) * v.amount; return; }
      if (v.type === 'income') { inc += v.amount; nInc++; }
      else { exp += v.amount; nExp++; if (v.card) card += v.amount; }
      items.push({ date: x.date, name: v.name, amount: v.amount, type: v.type, card: !!v.card, est: !!v.estimated });
    });
  });
  if (close === null) close = open;
  if (low === null) { low = open; lowDate = s; }
  return { open, close, low, lowDate, inc, exp, card, nInc, nExp, items, hasBal: hasBalance(), start: s, end: e };
}

/** הוצאות שוטפות מהעו"ש בתקופה רגילה (ביט, מזומן, הוראות חד-פעמיות): חציון 3 התקופות הקודמות שיש בהן עו"ש.
    בלי חיובים קבועים, חיוב הכרטיס, העברות לחיסכון ופעולות מוחרגות. פחות משתי תקופות: 0 */
export function cashVarTypical() {
  return memo('cashVarTypical', () => {
    const k = curKey(), isFixed = fixedMatcher(), byTx = recMatchAll().byTx, sums = [];
    for (let q = 1; q <= 3; q++) {
      const qk = keyAdd(k, -q);
      let has = false, s = 0;
      S.tx.forEach((t) => {
        if (t.month !== qk || getTxSrc(t) === 'credit') return;
        has = true;
        if (t.type !== 'expense' || isExcluded(t) || isCardChargeRow(t) || isFixed(t) || byTx[t.id] != null) return;
        s += absAmt(t);
      });
      if (has) sums.push(s);
    }
    return { amount: sums.length >= 2 ? Math.round(median(sums)) : 0, n: sums.length };
  });
}

/** חודש רגיל: הכנסות קבועות, חיובים קבועים מהעו"ש, חיוב כרטיס טיפוסי והוצאות שוטפות מהעו"ש.
    חיובים קבועים בכרטיס כבר בתוך חיוב הכרטיס. חיוב שנתי = 1/12 בחודש */
export function monthlyParts() {
  let inc = 0, exp = 0;
  const tpl = cardTemplate(), fe = cardFutureEstimate();
  const card = fe.amount || (cardChargeAmount() || {}).amount || 0;
  S.rec.forEach((r) => {
    const a = recEst(r) / recEvery(r);
    if (r.type === 'income') inc += a;
    else if ((r.source || 'cash') !== 'credit' && !isSavingRec(r) && !(card > 0 && r === tpl)) exp += a;
  });
  // vexpAll: ההערכה עצמה, גם במצב רגיל, כדי שההסבר יוכל להראות מה לא נכלל
  const vexpAll = cashVarTypical().amount;
  return { inc, exp, card, fe, vexp: strictMode() ? vexpAll : 0, vexpAll, strict: strictMode() };
}
export function monthlyNet() {
  const m = monthlyParts();
  return m.inc - m.exp - m.card - m.vexp;
}

/* ── טווח מסך התזרים, בתקופות (11 עד 10) ולא בימים ── */
export const FLOW_RANGES = [
  [1, 'התקופה הנוכחית'],
  [2, 'עד סוף התקופה הבאה'],
  [3, '3 תקופות'],
  [6, '6 תקופות'],
  [12, '12 תקופות'],
];
/** סוף התקופה ה-n מהיום. ביום האחרון של תקופה, הנוכחית כבר נגמרה, ולכן מתחילים מהבאה */
export function rangeEnd(n) {
  let k = curKey();
  if (dDays(periodEnd(k), todayISO()) < 1) k = keyAdd(k, 1);
  return periodEnd(keyAdd(k, n - 1));
}
export function rangeDays(n) {
  return Math.max(1, dDays(rangeEnd(n), todayISO()));
}
export function rangePhrase(n) {
  if (n === 1) return 'עד סוף התקופה';
  if (n === 2) return 'עד סוף התקופה הבאה';
  return 'ב-' + n + ' התקופות הקרובות';
}
/** תיאור טווח לפי ימים (להסברים שמקבלים מספר ימים) */
export function phraseForDays(d) {
  const m = [1, 2, 3, 6, 12].find((n) => rangeDays(n) === d);
  return m ? rangePhrase(m) : 'ב-' + d + ' הימים הקרובים';
}

/** סיכום התחזית לפי חיוב קבוע: כמה הוא מוסיף בטווח. מועד שעבר ועוד לא נסרק כבר בתוך "יתרה היום", ולכן לא כאן */
export function recInRange(days, opts = {}) {
  const out = {};
  project(days, opts).forEach((x) => x.events.forEach((v) => {
    if (v.recId == null || v.card || v.late) return;
    const o = (out[v.recId] = out[v.recId] || { sum: 0, n: 0, items: [] });
    o.sum += v.amount;
    o.n++;
    o.items.push({ d: x.date, name: v.name, amount: v.amount, late: !!v.late });
  }));
  return out;
}

/** רכיבי התחזית, מאותה ריצה בדיוק כמו המספרים במסך התזרים. מועד שעבר ועוד לא נסרק כבר בתוך "יתרה היום",
    ולכן הוא לא נספר שוב באף שורה. כך: יתרה היום + הכנסות − חיובים − כרטיס − השאר = היתרה בסוף הטווח */
export function projParts(days, opts = {}) {
  const p = project(days, opts), rs = {}, cards = [];
  let real = 0, commit = 0, variable = 0, nReal = 0, nVar = 0;
  p.forEach((x) => x.events.forEach((v) => {
    if (v.late) {
      if (v.recId != null) (rs[v.recId] = rs[v.recId] || { sum: 0, n: 0, items: [], late: 0 }).late++;
      return;
    }
    if (v.card) { cards.push({ d: x.date, a: v.amount, est: !!v.estimated, iso: v.iso }); return; }
    if (v.recId != null) {
      const o = (rs[v.recId] = rs[v.recId] || { sum: 0, n: 0, items: [], late: 0 });
      o.sum += v.amount; o.n++; o.items.push({ d: x.date, amount: v.amount });
      return;
    }
    if (v.variable) { variable += v.amount; nVar++; return; }
    if (v.commit) { commit += v.amount; return; }
    real += (v.type === 'income' ? -1 : 1) * v.amount; nReal++;
  }));
  return { p, rs, cards, real, commit, variable, nReal, nVar, end: p.length ? p[p.length - 1].balance : balEst().amount };
}
